import React, { useMemo, useState } from 'react';
import { XIcon, ExternalLinkIcon, FileTextIcon, ClockIcon, ShieldCheckIcon } from 'lucide-react';
import { BusinessHistory, BusinessEvent } from '../common/BusinessHistory';
import { RegulatoryAuditTrail, AuditEntry } from '../common/RegulatoryAuditTrail';
import { StatusBadge } from '../StatusBadge';
interface DocumentPreviewProps {
  document: any; // Library document type
  onClose?: () => void;
  onOpenDocument?: () => void;
  locale?: string;
}
export function DocumentPreview({
  document,
  onClose,
  onOpenDocument,
  locale = 'en-US'
}: DocumentPreviewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const [historySubTab, setHistorySubTab] = useState<'timeline' | 'audit'>('timeline');
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Generate mock history based on the library document
  const {
    businessEvents,
    auditEntries
  } = useMemo(() => {
    const createdDate = new Date(document.createdDate);
    const modifiedDate = new Date(document.lastModified);
    const bEvents: BusinessEvent[] = [{
      id: 'evt-1',
      timestamp: createdDate.toISOString(),
      description: `Document ${document.isImported ? 'imported' : 'created'}`,
      actor: document.createdBy,
      actorType: document.isImported ? 'system' : 'user',
      category: 'received',
      details: `${document.type} ${document.id} was ${document.isImported ? 'imported into' : 'created in'} the system.`
    }];
    const aEntries: AuditEntry[] = [{
      id: 'aud-1',
      timestamp: createdDate.toISOString(),
      actor: document.createdBy,
      actorType: document.isImported ? 'system' : 'user',
      category: 'action',
      actionType: 'DOCUMENT_CREATED',
      targetEntity: document.id,
      description: `${document.type} document created`
    } as AuditEntry];
    if (document.ocrConfidence) {
      const ocrDate = new Date(createdDate.getTime() + 5 * 60000);
      bEvents.push({
        id: 'evt-2',
        timestamp: ocrDate.toISOString(),
        description: 'Data extraction completed',
        actor: 'Document Intelligence',
        actorType: 'system',
        category: 'processing',
        details: `Extracted data with ${Math.round(document.ocrConfidence * 100)}% confidence.`
      });
      aEntries.push({
        id: 'aud-2',
        timestamp: ocrDate.toISOString(),
        actor: 'System',
        actorType: 'system',
        category: 'action',
        actionType: 'DATA_EXTRACTION',
        targetEntity: document.id,
        description: `OCR extraction completed with ${Math.round(document.ocrConfidence * 100)}% confidence`
      } as AuditEntry);
    }
    if (document.status === 'Approved' || document.status === 'Paid' || document.status === 'Completed') {
      bEvents.push({
        id: 'evt-3',
        timestamp: modifiedDate.toISOString(),
        description: `Document ${document.status.toLowerCase()}`,
        actor: 'Workflow Engine',
        actorType: 'system',
        category: 'approval',
        details: `Document reached ${document.status} status.`
      });
      aEntries.push({
        id: 'aud-3',
        timestamp: modifiedDate.toISOString(),
        actor: 'Workflow Engine',
        actorType: 'system',
        category: 'workflow_transition',
        previousState: 'Pending',
        newState: document.status,
        triggeredBy: 'Approval Workflow'
      } as AuditEntry);
    }
    return {
      businessEvents: bEvents,
      auditEntries: aEntries
    };
  }, [document]);
  const getStatusBadgeType = (status: string) => {
    switch (status) {
      case 'Approved':
      case 'Completed':
      case 'Paid':
      case 'Active':
      case 'Processed':
        return 'success';
      case 'Pending Approval':
      case 'Sent to Vendor':
        return 'info';
      case 'Draft':
        return 'default';
      case 'Exception':
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  return <div className="fixed inset-0 overflow-hidden z-50" data-id="element-5273">
      <div className="absolute inset-0 overflow-hidden" data-id="element-5274">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-5275" />
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16" data-id="element-5276">
          <div className="w-screen max-w-2xl" data-id="element-5277">
            <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-5278">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between sticky top-0 z-10" data-id="element-5279">
                <div className="flex items-center gap-3" data-id="element-5280">
                  <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600" data-id="element-5281">
                    <FileTextIcon className="w-6 h-6" data-id="element-5282" />
                  </div>
                  <div data-id="element-5283">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-5284">
                      {document.id}
                      <StatusBadge status={getStatusBadgeType(document.status)} text={document.status} data-id="element-5285" />
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-5286">{document.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-5287">
                  {onOpenDocument && <button onClick={onOpenDocument} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Open full document" data-id="element-5288">
                      <ExternalLinkIcon className="w-5 h-5" data-id="element-5289" />
                    </button>}
                  {onClose && <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-5290">
                      <XIcon className="w-5 h-5" data-id="element-5291" />
                    </button>}
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-gray-200 bg-white sticky top-[73px] z-10" data-id="element-5292">
                <nav className="-mb-px flex space-x-8" data-id="element-5293">
                  <button onClick={() => setActiveTab('overview')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5294">
                    Overview
                  </button>
                  <button onClick={() => setActiveTab('history')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5295">
                    History & Audit
                  </button>
                </nav>
              </div>

              {/* Content */}
              <div className="flex-1 p-6" data-id="element-5296">
                {activeTab === 'overview' && <div className="space-y-8" data-id="element-5297">
                    {/* Key Details Grid */}
                    <div className="grid grid-cols-2 gap-6" data-id="element-5298">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-5299">
                        <div className="text-xs text-gray-500 mb-1 uppercase font-semibold tracking-wider" data-id="element-5300">
                          Amount
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-id="element-5301">
                          {formatCurrency(document.amount)}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-5302">
                        <div className="text-xs text-gray-500 mb-1 uppercase font-semibold tracking-wider" data-id="element-5303">
                          Dates
                        </div>
                        <div className="text-sm text-gray-900" data-id="element-5304">
                          <span className="font-medium" data-id="element-5305">Date:</span>{' '}
                          {formatDate(document.date)}
                        </div>
                        {document.dueDate && <div className="text-sm text-gray-900 mt-1" data-id="element-5306">
                            <span className="font-medium" data-id="element-5307">Due:</span>{' '}
                            {formatDate(document.dueDate)}
                          </div>}
                      </div>
                    </div>

                    {/* Properties */}
                    <div data-id="element-5308">
                      <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2" data-id="element-5309">
                        Document Properties
                      </h3>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4" data-id="element-5310">
                        {document.vendor && <div data-id="element-5311">
                            <dt className="text-xs text-gray-500" data-id="element-5312">Vendor</dt>
                            <dd className="text-sm font-medium text-gray-900" data-id="element-5313">
                              {document.vendor}
                            </dd>
                          </div>}
                        <div data-id="element-5314">
                          <dt className="text-xs text-gray-500" data-id="element-5315">Entity</dt>
                          <dd className="text-sm font-medium text-gray-900" data-id="element-5316">
                            {document.entity}
                          </dd>
                        </div>
                        <div data-id="element-5317">
                          <dt className="text-xs text-gray-500" data-id="element-5318">Site</dt>
                          <dd className="text-sm font-medium text-gray-900" data-id="element-5319">
                            {document.site}
                          </dd>
                        </div>
                        <div data-id="element-5320">
                          <dt className="text-xs text-gray-500" data-id="element-5321">Created By</dt>
                          <dd className="text-sm font-medium text-gray-900" data-id="element-5322">
                            {document.createdBy}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Tags & Links */}
                    {(document.tags?.length > 0 || document.linkedDocuments?.length > 0) && <div data-id="element-5323">
                        <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2" data-id="element-5324">
                          Tags & Links
                        </h3>
                        <div className="space-y-4" data-id="element-5325">
                          {document.tags?.length > 0 && <div data-id="element-5326">
                              <div className="text-xs text-gray-500 mb-2" data-id="element-5327">
                                Tags
                              </div>
                              <div className="flex flex-wrap gap-2" data-id="element-5328">
                                {document.tags.map((tag: string) => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5329">
                                    {tag}
                                  </span>)}
                              </div>
                            </div>}
                          {document.linkedDocuments?.length > 0 && <div data-id="element-5330">
                              <div className="text-xs text-gray-500 mb-2" data-id="element-5331">
                                Linked Documents
                              </div>
                              <div className="flex flex-wrap gap-2" data-id="element-5332">
                                {document.linkedDocuments.map((link: string) => <span key={link} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100" data-id="element-5333">
                                      {link}
                                    </span>)}
                              </div>
                            </div>}
                        </div>
                      </div>}

                    {/* Document Preview Image */}
                    {document.previewUrl && <div data-id="element-5334">
                        <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2" data-id="element-5335">
                          Document Preview
                        </h3>
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100" data-id="element-5336">
                          <img src={document.previewUrl} alt={`Preview of ${document.id}`} className="w-full h-auto object-contain max-h-[600px]" data-id="element-5337" />
                        </div>
                      </div>}
                  </div>}

                {activeTab === 'history' && <div className="space-y-6" data-id="element-5338">
                    {/* Sub-tabs */}
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-id="element-5339">
                      <button onClick={() => setHistorySubTab('timeline')} className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${historySubTab === 'timeline' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5340">
                        <ClockIcon className="w-4 h-4" data-id="element-5341" />
                        Timeline
                      </button>
                      <button onClick={() => setHistorySubTab('audit')} className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${historySubTab === 'audit' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5342">
                        <ShieldCheckIcon className="w-4 h-4" data-id="element-5343" />
                        Audit Trail
                      </button>
                    </div>

                    {/* Content */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-5344">
                      {historySubTab === 'timeline' ? <BusinessHistory entityType={document.type} entityId={document.id} events={businessEvents} data-id="element-5345" /> : <RegulatoryAuditTrail entityType={document.type} entityId={document.id} auditEntries={auditEntries} hasAuditAccess={true} data-id="element-5346" />}
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}